import pick from "lodash/pick";
import moment from "moment";
import isEqual from "lodash/isEqual";
import { WeekDays } from "dash-common";

type TWorkingHours = Record<
  string,
  { from: string; to: string; __typename: string }
>;

const formatValues = (obj: any) =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, value || null])
  );

const getExistedWorkingHours = (workingHours: TWorkingHours) => {
  const existedWorkingHours = Object.entries(workingHours || {}).reduce(
    (memo, [key, value]) => {
      if (value) {
        const { __typename, ...rest } = value;

        return {
          ...memo,
          [key]: rest,
        };
      }
      return memo;
    },
    {}
  );

  return existedWorkingHours;
};

const compareSalesGalleryHours = (
  currentSalesGalleryHours: TWorkingHours,
  savedSalesGalleryHours: TWorkingHours
) => {
  const fieldsToPick = Object.values(WeekDays);

  const existedCurrentSalesGalleryHours = getExistedWorkingHours(
    currentSalesGalleryHours
  );
  const existedSavedSalesGalleryHours = getExistedWorkingHours(
    savedSalesGalleryHours
  );

  const currentSalesGalleryHoursToCompare = pick(
    existedCurrentSalesGalleryHours,
    fieldsToPick
  );
  const savedSalesGalleryHoursToCompare = pick(
    existedSavedSalesGalleryHours,
    fieldsToPick
  );

  return isEqual(
    currentSalesGalleryHoursToCompare,
    savedSalesGalleryHoursToCompare
  );
};

const compareDates = (formStateDate: string, propertyDate: string) => {
  const formStateDateMoment = moment(formStateDate);
  const propertyDateMoment = moment(propertyDate);

  if (!formStateDate || !propertyDate) {
    return formStateDate === propertyDate;
  }

  return formStateDateMoment.isSame(propertyDateMoment, "day");
};

export const compareAdvancedPropertyData = (
  property: any,
  formStateData: any
) => {
  const {
    errorSlug,
    isLoading,
    salesGalleryHours,
    completionDate,
    constructionStartDate,
    virtualTourLinks,
    ...rest
  } = formStateData;
  const fields = Object.keys(rest);
  const propertiesToPick = fields.reduce(
    (memo: any, key: any) => ({
      ...memo,
      [key]: property[key],
    }),
    {}
  );

  const propertyData = formatValues(pick(propertiesToPick, fields));
  const formState = formatValues(pick(rest, fields));

  // Filter out empty virtual tour links for comparison
  const filterValidLinks = (links: any[] | undefined) => {
    if (!links) return [];
    return links.filter(link => link && link.url && link.url.trim() !== '');
  };

  return (
    isEqual(propertyData, formState) &&
    compareDates(completionDate, property.completionDate) &&
    compareDates(constructionStartDate, property.constructionStartDate) &&
    compareSalesGalleryHours(salesGalleryHours, property?.salesGalleryHours) &&
    isEqual(filterValidLinks(virtualTourLinks), filterValidLinks(property?.virtualTourLinks))
  );
};
